import { action } from '@ember/object';
import type { Registry as Services } from '@ember/service';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import PrismTable from '@mylighthouse/prism-ember/components/prism/table';
import type {
  CustomEmberTableColumn,
  CustomEmberTableRow,
} from '@mylighthouse/prism-ember/types/components/prism/table';
import dayjs, { type Dayjs } from 'dayjs';
import MarketSnapshotsTableTdMonth from 'lumen/components/market/snapshots/table/td-month';
import MarketSnapshotsTableTdName from 'lumen/components/market/snapshots/table/td-name';
import MarketSnapshotsTableTdWithVariance, {
  type MarketSnapshotsTableTdWithVarianceSignature,
} from 'lumen/components/market/snapshots/table/td-with-variance';
import type {
  MarketPerformanceDataItem,
  MarketPerformanceDataStatus,
} from 'lumen/models';
import type {
  Kpi,
  MarketPerformanceMetrics,
  MarketPerformanceSegments,
  MarketPerformanceTotals,
  MetricType,
  TableKpi,
} from 'lumen/models/market-performance/data-item';
import {
  GROUP_BY,
  GROUP_COLUMNS_BY,
  type GroupBy,
  type GroupColumnsBy,
  KPI_TRANSLATIONS,
  METRIC_TYPE_TRANSLATIONS,
  segmentMetricType,
  TABLE_KPI_TRANSLATIONS,
} from 'lumen/utils/constants/market-snapshots-constants';
import { type Variance, VARIANCES } from 'lumen/utils/constants/variance';
import { orderByKey } from 'lumen/utils/market-snapshots/order-by';

import styles from './table.module.css';

export interface MarketSnapshotsTableSignature {
  Args: {
    dataItems: MarketPerformanceDataItem[];
    dateRange: { end: Dayjs; start: Dayjs };
    groupBy?: GroupBy;
    groupColumnsBy: GroupColumnsBy;
    isLoading?: boolean;
    /** Defaults to sortable; month rows never are. */
    isSortable?: boolean;
    /** The sub-columns of each column, when they don't all share `visibleKpis`. */
    kpisFor?: (metricType: MetricType) => readonly TableKpi[];
    onSort: (orderBy: string) => void;
    orderBy: string;
    totals?: MarketPerformanceTotals;
    varianceType: Variance;
    visibleKpis: readonly Kpi[];
    visibleMetricTypes: MetricType[];
  };
}

type CellData = MarketSnapshotsTableTdWithVarianceSignature['Args']['cellData'];

type MetricDisplayValue = Partial<Record<TableKpi, CellData>>;

type FormatNumber = (value: number) => string;

// A pickup row's `variance.absolute` is the change since the pickup date. OTB
// pickup ADR is the picked-up rooms' ADR (revenue change / rooms change).
function buildPickupDisplayValue(
  totals: MarketPerformanceMetrics,
  metricType: MetricType,
): MetricDisplayValue {
  const delta = (kpi: Kpi): number | null =>
    totals.variance?.[kpi].absolute ?? null;
  const rooms = delta('rooms');
  const revenue = delta('revenue');

  return {
    adr: metricType.startsWith('otb:')
      ? { value: rooms && revenue !== null ? revenue / rooms : null }
      : { isDelta: true, value: delta('adr') },
    occupancy: {
      isDelta: true,
      isValuePercent: true,
      value: delta('occupancy'),
    },
    revenue: { isDelta: true, value: revenue },
    revpar: { isDelta: true, value: delta('revpar') },
    rooms: { isDelta: true, value: rooms },
  };
}

function buildMetricDisplayValue(
  totals: MarketPerformanceMetrics,
  metricType: MetricType,
  varianceType: Variance,
): MetricDisplayValue {
  if (metricType.includes(':pickup:')) {
    return buildPickupDisplayValue(totals, metricType);
  }

  const varianceKey = varianceType === 'absolute' ? 'absolute' : 'percent';
  const resolvedVarianceType =
    metricType === 'otb' ? VARIANCES.NONE : varianceType;

  return {
    adr: {
      value: totals.adr,
      varianceType: resolvedVarianceType,
      varianceValue: totals.variance?.adr[varianceKey],
    },
    occupancy: {
      isValuePercent: true,
      value: totals.occupancy,
      varianceType: resolvedVarianceType,
      varianceValue: totals.variance?.occupancy[varianceKey],
    },
    revenue: {
      value: totals.revenue,
      varianceType: resolvedVarianceType,
      varianceValue: totals.variance?.revenue[varianceKey],
    },
    revpar: {
      value: totals.revpar,
      varianceType: resolvedVarianceType,
      varianceValue: totals.variance?.revpar[varianceKey],
    },
    rooms: {
      value: totals.rooms,
      varianceType: resolvedVarianceType,
      varianceValue: totals.variance?.rooms[varianceKey],
    },
  };
}

// Group cells: ADR and revenue of the group OTB; "OTB/BLK" is both rooms with
// the OTB variance, and the blocked revenue has its own variance.
function buildGroupDisplayValue(
  {
    groupBlk,
    groupOtb,
  }: {
    groupBlk: MarketPerformanceMetrics | null;
    groupOtb: MarketPerformanceMetrics;
  },
  metricType: MetricType,
  varianceType: Variance,
  formatNumber: FormatNumber,
): MetricDisplayValue {
  const { adr, revenue, rooms } = buildMetricDisplayValue(
    groupOtb,
    metricType,
    varianceType,
  );
  const blocked = groupBlk
    ? buildMetricDisplayValue(groupBlk, metricType, varianceType).revenue
    : undefined;

  return {
    adr,
    otb_blk: {
      text: groupBlk
        ? `${formatNumber(groupOtb.rooms)} / ${formatNumber(groupBlk.rooms)}`
        : undefined,
      value: groupOtb.rooms,
      varianceType: rooms?.varianceType,
      varianceValue: rooms?.varianceValue,
    },
    rev_blk: blocked ?? { value: null },
    revenue,
  };
}

// Adds the transient and group cells of a row or total next to its own, keyed
// by their segment metric type (`transient`, `group:sdly`...).
function addSegmentDisplayValues(
  displayValues: Partial<Record<MetricType, MetricDisplayValue>>,
  metricType: MetricType,
  { group_blk, group_otb, transient }: MarketPerformanceSegments,
  {
    formatNumber,
    varianceType,
  }: { formatNumber: FormatNumber; varianceType: Variance },
): void {
  if (transient) {
    displayValues[segmentMetricType('transient', metricType)] =
      buildMetricDisplayValue(transient, metricType, varianceType);
  }

  if (group_otb) {
    displayValues[segmentMetricType('group', metricType)] =
      buildGroupDisplayValue(
        { groupBlk: group_blk ?? null, groupOtb: group_otb },
        metricType,
        varianceType,
        formatNumber,
      );
  }
}

export default class MarketSnapshotsTable extends Component<MarketSnapshotsTableSignature> {
  @service declare dates: Services['dates'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];

  formatNumber = (value: number): string =>
    this.intl.formatNumber(value, {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0,
    });

  get canSort(): boolean {
    return this.args.isSortable !== false && !this.isMonthView;
  }

  get columns(): CustomEmberTableColumn[] {
    const { groupColumnsBy, visibleKpis, visibleMetricTypes } = this.args;

    const nameColumn: CustomEmberTableColumn = {
      hasRightBorder: true,
      isFixed: 'left',
      isSortable: this.canSort,
      minWidth: 200,
      name: this.intl.t(this.isMonthView ? 'general.month' : 'general.name'),
      tdComponent: this.isMonthView
        ? MarketSnapshotsTableTdMonth
        : MarketSnapshotsTableTdName,
      valuePath: 'name',
      width: 200,
    };

    if (groupColumnsBy === GROUP_COLUMNS_BY.KPIS) {
      return [
        nameColumn,
        ...visibleKpis.map(
          (kpi): CustomEmberTableColumn => ({
            name: this.intl.t(KPI_TRANSLATIONS[kpi]),
            subcolumns: visibleMetricTypes.map((metricType, index) =>
              this.buildCellColumn({
                hasRightBorder: index === visibleMetricTypes.length - 1,
                kpi,
                metricType,
                name: this.intl.t(METRIC_TYPE_TRANSLATIONS[metricType]),
              }),
            ),
            valuePath: '',
          }),
        ),
      ];
    }

    return [
      nameColumn,
      ...visibleMetricTypes.map((metricType): CustomEmberTableColumn => {
        const kpis = this.args.kpisFor?.(metricType) ?? visibleKpis;

        return {
          name: this.intl.t(METRIC_TYPE_TRANSLATIONS[metricType]),
          subcolumns: kpis.map((kpi, index) =>
            this.buildCellColumn({
              hasRightBorder: index === kpis.length - 1,
              kpi,
              metricType,
              name: this.intl.t(TABLE_KPI_TRANSLATIONS[kpi]),
            }),
          ),
          valuePath: '',
        };
      }),
    ];
  }

  // Compared as `YYYY-MM` strings: month dates are UTC, "now" is the hotel's
  // local time, so an instant comparison is off around the month boundary.
  get currentMonth(): string {
    return this.hotelAndMarketService.getLocalTime().format('YYYY-MM');
  }

  get footerRows(): CustomEmberTableRow[] {
    const { totals, varianceType } = this.args;

    if (!totals) {
      return [];
    }

    const metricTypeToDisplayValues: Partial<
      Record<MetricType, MetricDisplayValue>
    > = {};

    for (const [metricType, item] of Object.entries(totals) as [
      MetricType,
      MarketPerformanceTotals[MetricType],
    ][]) {
      if (item.totals) {
        metricTypeToDisplayValues[metricType] = buildMetricDisplayValue(
          item.totals,
          metricType,
          varianceType,
        );
      }

      addSegmentDisplayValues(metricTypeToDisplayValues, metricType, item, {
        formatNumber: this.formatNumber,
        varianceType,
      });
    }

    return [
      {
        displayValue: metricTypeToDisplayValues,
        name: this.intl.t('general.total'),
      },
    ];
  }

  get isMonthView(): boolean {
    return this.args.groupBy === GROUP_BY.MONTH;
  }

  // Month rows show the year only when the date range spans more than one
  // calendar year.
  get monthLabelFormat(): string {
    const { end, start } = this.args.dateRange;

    return start.year() !== end.year()
      ? this.dates.partialDateFormat.YEAR_AND_MONTH_LONG
      : 'MMMM';
  }

  get rows(): CustomEmberTableRow[] {
    const groups = new Map<
      string,
      {
        dataStatus: MarketPerformanceDataStatus | null;
        isCurrentMonth: boolean;
        metricTypeToDisplayValues: Partial<
          Record<MetricType, MetricDisplayValue>
        >;
        name: string;
      }
    >();

    for (const item of this.args.dataItems) {
      const { date, subscription } = item;
      const key = (this.isMonthView ? date : subscription?.id) ?? '';

      if (this.isMonthView && !key) {
        continue;
      }

      const group = groups.get(key) ?? {
        dataStatus: subscription?.dataStatus ?? null,
        isCurrentMonth:
          this.isMonthView &&
          dayjs.utc(date).format('YYYY-MM') === this.currentMonth,
        metricTypeToDisplayValues: {},
        name: this.isMonthView
          ? dayjs.utc(date).format(this.monthLabelFormat)
          : (subscription?.hotelName ?? ''),
      };

      // The API sends `totals: null` for metrics without data (e.g. no user
      // forecast); the row still renders, with an empty cell.
      if (item.totals) {
        group.metricTypeToDisplayValues[item.metricType] =
          buildMetricDisplayValue(
            item.totals,
            item.metricType,
            this.args.varianceType,
          );
      }

      addSegmentDisplayValues(
        group.metricTypeToDisplayValues,
        item.metricType,
        {
          group_blk: item.groupBlk,
          group_otb: item.groupOtb,
          transient: item.transient,
        },
        {
          formatNumber: this.formatNumber,
          varianceType: this.args.varianceType,
        },
      );

      groups.set(key, group);
    }

    return [...groups.values()].map(
      ({ dataStatus, isCurrentMonth, metricTypeToDisplayValues, name }) => ({
        dataStatus,
        displayValue: metricTypeToDisplayValues,
        isCurrentMonth,
        name,
      }),
    );
  }

  get sorts(): { isAscending: boolean; valuePath: string }[] {
    const { orderBy } = this.args;
    const isDescending = orderBy.startsWith('-');

    // Month rows come back in date order from the API and can't be sorted.
    if (!this.canSort) {
      return [];
    }

    return [
      {
        valuePath: isDescending ? orderBy.slice(1) : orderBy,
        isAscending: !isDescending,
      },
    ];
  }

  buildCellColumn({
    hasRightBorder,
    kpi,
    metricType,
    name,
  }: {
    hasRightBorder: boolean;
    kpi: TableKpi;
    metricType: MetricType;
    name: string;
  }): CustomEmberTableColumn {
    return {
      displayValuePath: `displayValue.${metricType}.${kpi}`,
      hasRightBorder,
      isSortable: this.canSort,
      name,
      tdComponent: MarketSnapshotsTableTdWithVariance,
      textAlign: 'right',
      valuePath: orderByKey(metricType, kpi),
    };
  }

  @action
  onUpdateSorts(sorts: { isAscending: boolean; valuePath: string }[]): void {
    const sort = sorts[0];

    if (!sort) {
      // Empty array = ember-table's "unsorted" state. Toggle direction instead.
      const { orderBy } = this.args;
      const isDescending = orderBy.startsWith('-');

      this.args.onSort(isDescending ? orderBy.slice(1) : `-${orderBy}`);

      return;
    }

    const orderBy = sort.isAscending ? sort.valuePath : `-${sort.valuePath}`;

    this.args.onSort(orderBy);
  }

  <template>
    <PrismTable
      @columns={{this.columns}}
      @footerRows={{this.footerRows}}
      @isLoading={{@isLoading}}
      @onUpdateSorts={{this.onUpdateSorts}}
      @removeTableBorder={{true}}
      @rows={{this.rows}}
      @sorts={{this.sorts}}
      class={{styles.table}}
      data-test-table
    />
  </template>
}
